import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import AccessibilityAlert from './AccessibilityAlert';

const AccessibilityPortal: React.FC = () => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const observerRef = useRef<MutationObserver | null>(null);
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        // Create container and append directly to body
        containerRef.current = document.createElement('div');
        containerRef.current.id = 'accessibility-host';
        containerRef.current.setAttribute('data-accessibility-host', '');
        document.body.appendChild(containerRef.current);

        // Set up mutation observer to remove aria-hidden
        observerRef.current = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' &&
                    mutation.attributeName === 'aria-hidden' &&
                    containerRef.current) {
                    containerRef.current.removeAttribute('aria-hidden');
                }
            });
        });

        if (containerRef.current) {
            observerRef.current.observe(containerRef.current, {
                attributes: true,
                attributeFilter: ['aria-hidden']
            });
        }

        setIsReady(true);

        // Cleanup
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }

            if (containerRef.current) {
                document.body.removeChild(containerRef.current);
            }
        };

    }, []);

    if (!isReady || !containerRef.current) {
        return null;
    }

    return createPortal(
        <AccessibilityAlert />,
        containerRef.current
    );
};

export const ProtectedAccessibilityAlert: React.FC = () => (
    <AccessibilityPortal />
);